import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Mail, Phone, Github, Linkedin, Globe, Download, Briefcase } from 'lucide-react';
import StatCounter from './StatCounter';

function LangTransition({ children, lang, className = "" }) {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={lang}
        className={className}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.2 }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

const Header = function({ lang, setLang, theme, setTheme, personalInfo, socialLinks, stats, handleDownload }) {
  const isDark = theme === "dark";

  return (
    <header
      id="header"
      className={`relative py-10 px-6 sm:px-10 mb-8 border-b ${isDark ? 'border-white/10' : 'border-slate-200'} flex flex-col md:flex-row md:items-end md:justify-between gap-8 print:border-slate-300 print:pb-4`}
      role="banner"
    >
      {/* Top Controls - absolute positioning so it doesn't mess with natural flex layout */}
      <div className="absolute top-4 right-4 flex gap-2 items-center print:hidden" role="toolbar" aria-label="Settings">
        <button
          onClick={() => setTheme(isDark ? "light" : "dark")}
          className={`p-1.5 rounded-md transition-all ${isDark ? 'bg-white/10 hover:bg-white/20 text-slate-300' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'}`}
          aria-label="Toggle theme"
        >
          {isDark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
        </button>
        <div className={`h-4 w-px ${isDark ? 'bg-white/20' : 'bg-slate-300'}`} />
        <button
          onClick={() => setLang('en')}
          className={`px-2 py-1 rounded-md text-[10px] uppercase font-bold transition-all ${lang === 'en' ? 'bg-blue-600 text-white' : isDark ? 'text-slate-400 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-200'}`}
        >
          EN
        </button>
        <button
          onClick={() => setLang('th')}
          className={`px-2 py-1 rounded-md text-[10px] uppercase font-bold transition-all ${lang === 'th' ? 'bg-blue-600 text-white' : isDark ? 'text-slate-400 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-200'}`}
        >
          TH
        </button>
      </div>

      <div className="flex-1">
        <LangTransition lang={lang}>
          <h1
            className={`text-3xl sm:text-4xl font-bold tracking-tight leading-snug mb-3 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}
            style={lang === 'en' ? { fontFamily: "'Playfair Display', serif" } : undefined}
          >
            {personalInfo.name}
          </h1>
        </LangTransition>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs tracking-wide font-semibold uppercase mb-5 ${isDark ? 'border-blue-400/30 bg-blue-500/10 text-blue-300' : 'border-blue-200 bg-blue-50 text-blue-700'}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-current" />
          {personalInfo.roles[0]}
        </span>

        {/* Links section */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          {socialLinks.github && (
            <a href={socialLinks.github} target="_blank" rel="noreferrer" className={`flex items-center gap-1.5 hover:underline ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
              <Github className="w-3.5 h-3.5" /><span>Open Source Activity</span>
            </a>
          )}
          {socialLinks.portfolio && (
            <a href={socialLinks.portfolio} target="_blank" rel="noreferrer" className={`flex items-center gap-1.5 hover:underline ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
              <Globe className="w-3.5 h-3.5" /><span>Portfolio</span>
            </a>
          )}
          {socialLinks.linkedin && (
            <a href={socialLinks.linkedin} target="_blank" rel="noreferrer" className={`flex items-center gap-1.5 hover:underline ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
              <Linkedin className="w-3.5 h-3.5" /><span>LinkedIn</span>
            </a>
          )}
          {socialLinks.fastwork && (
            <a href={socialLinks.fastwork} target="_blank" rel="noopener" title="DevHub on Fastwork — Freelance Web Development" className={`flex items-center gap-1.5 hover:underline ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
              <Briefcase className="w-3.5 h-3.5" /><span>Hire me on Fastwork</span>
            </a>
          )}
          {personalInfo.email && (
            <a href={`mailto:${personalInfo.email}`} className={`flex items-center gap-1.5 hover:underline ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
              <Mail className="w-3.5 h-3.5" /><span>Email</span>
            </a>
          )}
          {personalInfo.phone && (
            <div className={`flex items-center gap-1.5 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              <Phone className="w-3.5 h-3.5" /><span>{personalInfo.phone}</span>
            </div>
          )}
        </div>

        {/* Stats */}
        {stats && stats.length > 0 && (
          <div className="flex flex-wrap gap-x-10 gap-y-4 mt-7">
            {stats.map((s, i) => (
              <div key={i} className="flex flex-col">
                <span className="text-3xl sm:text-4xl font-extrabold leading-none bg-gradient-to-r from-blue-500 to-emerald-500 bg-clip-text text-transparent">
                  <StatCounter value={s.value} suffix={s.suffix} />
                </span>
                <span className={`text-[11px] uppercase tracking-wider font-semibold mt-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 print:hidden">
        <button
          onClick={() => handleDownload("cv")}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-all ${isDark ? 'border-white/20 hover:bg-white/10 text-white' : 'border-slate-300 hover:bg-slate-100 text-slate-800'}`}
        >
          <Download className="w-3.5 h-3.5" /> CV
        </button>
      </div>
    </header>
  );
}

export default React.memo(Header);
